import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { debounceTime, distinctUntilChanged, skip, take, tap } from 'rxjs/operators';
import { forkJoin, fromEvent, merge, Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { TemplateEditComponent } from '../template-edit/template-edit.component';
import { ActivatedRoute } from '@angular/router';
import { Template } from '../../../../../../core/lists/_models/template.model';
import {
  CategoryService,
  ClearTemplateState,
  selectTemplateError,
  selectTemplateLatestSuccessfullAction,
  selectTemplatesPageLastQuery,
  TemplateActionTypes,
  TemplateOnServerDeleted,
  TemplatesDataSource,
  TemplatesPageRequested,
} from '../../../../../../core/lists';
import {
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
} from '../../../../../../core/_base/crud';
import { AppState } from '../../../../../../core/reducers';
import { StandardService } from '../../../../compliance/services';
import { TransactionType } from '../../../../compliance/enums';
import { TemplateService } from '../../../../../../core/lists/_services/template.service';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { TemplateDuplicateComponent } from '../template-duplicate/template-duplicate.component';
import { TemplateBehaviorComponent } from '../template-behavior/template-behavior.component';
import { GeneralActionDialogComponent } from '../../../../../partials/content/crud/general-action-dialog/general-action-dialog.component';
import { currentUser } from '../../../../../../core/auth';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-templates-list',
  templateUrl: './templates-list.component.html',
  styleUrls: ['./templates-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplatesListComponent implements OnInit, OnDestroy {
  dataSource: TemplatesDataSource;
  displayedCols = [
    'id',
    'name',
    'categoryName',
    'isBackstage',
    'isIntervention',
    'additionalOnly',
    'allRoles',
    'resources',
    'actions',
  ];
  searchBarCols = [
    'searchId',
    'searchName',
    'searchCategory',
    'searchIsBackstage',
    'searchIsIntervention',
    'searchIsAdditionalStep',
    'searchAllRoles',
    'searchHasResources',
    'searchActions',
  ];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @ViewChild('searchIdInput') searchIdInput: ElementRef;
  @ViewChild('searchNameInput') searchNameInput: ElementRef;
  getCategoryAutocomplete;
  @ViewChild('searchCategoryAutocomplete') searchCategoryAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('searchIsBackstageSelect') searchIsBackstageSelect: MatSelect;
  @ViewChild('searchIsInterventionSelect') searchIsInterventionSelect: MatSelect;
  @ViewChild('searchIsAdditionalStepSelect') searchIsAdditionalStepSelect: MatSelect;
  @ViewChild('searchAllRolesSelect') searchAllRolesSelect: MatSelect;
  @ViewChild('searchHasResourcesSelect') searchHasResourcesSelect: MatSelect;
  lastQuery: QueryParamsModel;
  templatesResult: Template[] = [];
  private subscriptions: Subscription[] = [];
  userInfo: any;

  constructor(
    public dialog: MatDialog,
    private layoutUtilsService: LayoutUtilsService,
    private store: Store<AppState>,
    private translate: TranslateService,
    private activatedRoute: ActivatedRoute,
    private categoryService: CategoryService,
    private standardService: StandardService,
    private templateService: TemplateService,
  ) {
    this.store.select(currentUser).subscribe((res) => (this.userInfo = res));
  }

  ngOnInit() {
    // Init DataSource
    this.dataSource = new TemplatesDataSource(this.store);
    const dataSourceSub = this.dataSource.entitySubject.subscribe(
      (res) => (this.templatesResult = res),
    );
    this.subscriptions.push(dataSourceSub);
    const errorSubscription = this.store
      .pipe(select(selectTemplateError), skip(1))
      .subscribe((res) => {
        if (res) {
          this.layoutUtilsService.showActionNotification(
            this.translate.instant('GLOBAL.UNSPECIFIED_ERROR'),
            MessageType.Delete,
            3000,
            true,
            false,
          );
        }
      });
    this.subscriptions.push(errorSubscription);
    const successSubscription = this.store
      .pipe(select(selectTemplateLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          let successMsg;
          let msgType;
          switch (res.action_type) {
            case TemplateActionTypes.TemplateCreated:
              successMsg = this.translate.instant('TASK_TEMPLATE.ADD_MESSAGE');
              msgType = MessageType.Create;
              break;
            case TemplateActionTypes.TemplateUpdated:
              successMsg = this.translate.instant('TASK_TEMPLATE.UPDATE_MESSAGE');
              msgType = MessageType.Update;
              break;
            case TemplateActionTypes.TemplateDeleted:
              successMsg = this.translate.instant('TASK_TEMPLATE.DELETE_MESSAGE');
              msgType = MessageType.Delete;
              break;
          }
          this.layoutUtilsService.showActionNotification(successMsg, msgType, 3000, true, false);
        }
      });
    this.subscriptions.push(successSubscription);
    // If the user changes the sort order, reset back to the first page.
    const sortSubscription = this.sort.sortChange.subscribe((res) => {
      this.paginator.pageIndex = 0;

      if (!res.direction) {
        this.sort.direction = '';
        this.sort.active = '';
      }
    });
    this.subscriptions.push(sortSubscription);

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(tap(() => this.loadTemplatesList()))
      .subscribe();
    this.subscriptions.push(paginatorSubscriptions);

    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(150),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadTemplatesList();
        }),
      )
      .subscribe();
    this.subscriptions.push(searchSubscription);
    const routeSubscription = this.activatedRoute.queryParams.subscribe((params) => {
      if (params.id) {
        this.restoreState(this.lastQuery);
      }
      setTimeout(() => this.loadTemplatesList());
    });
    this.subscriptions.push(routeSubscription);

    const lastQuerySubscription = this.store
      .pipe(select(selectTemplatesPageLastQuery))
      .subscribe((res) => (this.lastQuery = res));
    // Load last query from store
    this.subscriptions.push(lastQuerySubscription);

    this.getCategoryAutocomplete = this.categoryService.getCategoriesInTemplateByUserType.bind(
      this.categoryService,
      this.userInfo?.userType,
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.store.dispatch(new ClearTemplateState());
  }

  loadTemplatesList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );
    this.store.dispatch(new TemplatesPageRequested({ page: queryParams }));
  }

  filterConfiguration(): any {
    const filter: any = {};

    if (this.showSearchBar) {
      filter.id = this.searchIdInput?.nativeElement?.value;
      filter.name = this.searchNameInput?.nativeElement?.value;
      filter.categoryId = this.searchCategoryAutocomplete?.value?.id;
      filter.isBackstage = this.searchIsBackstageSelect?.value;
      filter.isIntervention = this.searchIsInterventionSelect?.value;
      filter.additionalStepOnly = this.searchIsAdditionalStepSelect?.value;
      filter.allRoles = this.searchAllRolesSelect?.value;
      filter.hasResources = this.searchHasResourcesSelect?.value;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
    }

    return filter;
  }

  restoreState(queryParams: QueryParamsModel) {
    if (!queryParams.filter) {
      return;
    }
    if (queryParams.filter.model) {
      this.searchInput.nativeElement.value = queryParams.filter.model;
    }
  }

  deleteTemplate(_item: Template) {
    const _title: string = this.translate.instant('TASK_TEMPLATE.DELETE.TITLE');
    const _description: string = this.translate.instant('TASK_TEMPLATE.DELETE.DESCRIPTION');
    const _waitDescription: string = this.translate.instant(
      'TASK_TEMPLATE.DELETE.WAIT_DESCRIPTION',
    );
    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDescription);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.store.dispatch(new TemplateOnServerDeleted({ id: _item.id }));
    });
  }

  addTemplate() {
    if (this.userInfo?.userType !== 'Admin' && !this.userInfo?.is_manager) {
      this.dialog.open(GeneralActionDialogComponent, {
        width: '400px',
        disableClose: false,
        closeOnNavigation: true,
        data: {
          description: `
            <div style="height: 200px" class="d-flex flex-column flex-center">
              <i style="font-size: 80px" class="flaticon-warning text-danger"></i>
              <p class="font-weight-bold text-center">You are not allowed to create templates.</p>
              <label class="font-weight-bold text-muted">Access Denied</label>
            </label>
          `,
        },
      });
      return;
    }
    const newTemplate = new Template();
    this.updateTemplate(newTemplate);
  }

  duplicateTemplate(template: Template) {
    const dialogRef = this.templateService.openDialog(TemplateDuplicateComponent, {
      disableClose: true,
      closeOnNavigation: true,
      width: '650px',
      data: template,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) return;

        this.layoutUtilsService.showActionNotification(
          this.translate.instant('TASK_TEMPLATE.DUPLICATE_MESSAGE'),
          MessageType.Update,
          3000,
          false,
        );
        this.loadTemplatesList();
      });
  }

  updateTemplate(template: Template) {
    if (!template.id)
      return this.templateService
        .openDialog(TemplateEditComponent, {
          width: '97vw',
          disableClose: true,
          closeOnNavigation: true,
          data: { template, isReadOnly: false, mode: 'create' },
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe((res) => {
          if (!res) return;
          this.loadTemplatesList();
        });

    forkJoin({
      standards: this.standardService.getStandardListByTransaction(
        template.id,
        TransactionType.TASK_TEMPLATE,
      ),
      category: this.categoryService.getCategoryById(template.category_id),
    })
      .pipe(take(1))
      .subscribe((res) => {
        const prevStandards = res.standards || [];
        const prevCategory = res.category && res.category.data;
        const dialogRef = this.templateService.openDialog(TemplateEditComponent, {
          width: '97vw',
          disableClose: true,
          closeOnNavigation: true,
          data: { template, prevCategory, prevStandards, mode: 'update' },
        });

        dialogRef
          .afterClosed()
          .pipe(take(1))
          .subscribe(() => {
            this.loadTemplatesList();
          });
      });
  }

  formatCategoryOption(category): string {
    return category?.name || '';
  }

  reloadTemplatesList(): void {
    this.paginator.pageIndex = 0;

    this.loadTemplatesList();
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();

    this.searchIdInput.nativeElement.value = '';
    this.searchNameInput.nativeElement.value = '';
    this.searchCategoryAutocomplete?.setValue(null);
    this.searchCategoryAutocomplete?.updateValueAndValidity();
    this.searchIsBackstageSelect.value = '';
    this.searchIsInterventionSelect.value = '';
    this.searchIsAdditionalStepSelect.value = '';
    this.searchAllRolesSelect.value = '';
    this.searchHasResourcesSelect.value = '';
  }

  resetInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadTemplatesList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.resetSearchBar();
    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
      setTimeout(() => this.searchIdInput.nativeElement.focus());
    } else {
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadTemplatesList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();

    this.reloadTemplatesList();
  }

  setBehavior(template) {
    const dialogRef = this.dialog.open(TemplateBehaviorComponent, {
      width: '50vw',
      height: '80vh',
      data: { templateBehavior: template.behavior },
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      const cloneTemplate = { ...template };
      cloneTemplate.behavior = res.behavior;
      this.templateService.updateTemplate(cloneTemplate).subscribe(() => {
        this.loadTemplatesList();
        this.layoutUtilsService.showActionNotification(
          'Template behavior updated successfully',
          MessageType.Update,
          3000,
          false,
          false,
        );
      });
    });
  }
}
